import { Button, Card, Form, Input, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Dispute = {
  id: string;
  demand_id: string;
  student_id: string;
  reason: string;
  status: string;
  period_key?: string;
  demand_amount?: number;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  resolution_notes?: string | null;
};

export default function FeeDisputesPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canDispute = Boolean(
    user?.permissions?.includes('education.fee.dispute') ||
      user?.permissions?.includes('education.fee.collect') ||
      user?.permissions?.includes('education.fee.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-fee-disputes'],
    queryFn: () => api.get<Dispute[]>('/education/fees/disputes'),
  });

  const submit = useMutation({
    mutationFn: (values: { demand_id: string; reason: string }) =>
      api.post('/education/fees/disputes', values),
    onSuccess: () => {
      message.success(t('feeDisputeSubmitted'));
      queryClient.invalidateQueries({ queryKey: ['education-fee-disputes'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('feeDisputes')}
      description={t('feeDisputesIntro')}
      extra={<Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={query.isLoading}
            dataSource={query.data?.data ?? []}
            pagination={{ pageSize: 20 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: Dispute) =>
                  `${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim(),
              },
              { title: t('periodKey'), dataIndex: 'period_key', width: 110 },
              { title: t('reason'), dataIndex: 'reason', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (v: string) => <Tag color={v === 'open' ? 'orange' : undefined}>{v}</Tag>,
              },
              {
                title: t('actions'),
                width: 120,
                render: (_: unknown, row: Dispute) =>
                  row.status === 'open' && canDispute ? (
                    <Button size="small" onClick={() => navigate(`/education/fees/disputes/${row.id}/resolve`)}>
                      {t('resolve')}
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Card>
        {canDispute ? (
          <Card size="small" title={t('submitFeeDispute')}>
            <Form layout="vertical" style={{ maxWidth: 520 }} onFinish={(values) => submit.mutate(values)}>
              <Form.Item name="demand_id" label={t('feeDemand')} rules={[{ required: true }]}>
                <Input placeholder={t('demandIdHint')} />
              </Form.Item>
              <Form.Item name="reason" label={t('reason')} rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submit.isPending}>
                {t('submitForApproval')}
              </Button>
            </Form>
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
